// Field help for a customer's Billing tab (rate plan assignment, who pays whom, cost visibility,
// trials and credits, usage adjustments, the customer's invoices) and the invoice page. Keys are
// the field ids passed to <Field help="…">; every rendered label must have one
// (tests/unit/help.test.ts). Copy follows docs/guides/revenue-billing.md.
import type { HelpTopic } from "../topic";

export const TERMS_TOPICS = {
  "terms.invoices": {
    title: "Invoices",
    body: "The customer's months, newest first. An open month is a live preview; closing it fixes the invoice. Billed, AWS cost (at list price) and profit come from the revenue report; a not-billed month has no invoice.",
    example: "August 2026, Closed: billed $5,027.57, AWS cost $3,867.36, profit $1,160.21.",
    article: "closing-a-month",
    section: "invoices-and-statements",
  },
  "terms.ratePlan": {
    title: "Rate plan",
    body: "The plan that turns the customer's AWS cost into its price this month: a markup, per-model rates, tiers, a fee or a minimum. It is the customer's own assignment, or the fleet default when it has none for the month.",
    example: "“Demo cost + 30%”: AWS list price plus 30 %, so $1,000 of usage bills $1,300.",
    article: "rate-plans",
    section: "assigning-a-plan",
  },
  "terms.whoPaysWhom": {
    title: "Who pays whom",
    body: "Two month-effective settings: whether the customer is billed at all (not billed is proxy only: metered, never invoiced) and whose AWS bill its Bedrock usage lands on. Who pays AWS never changes what the customer is charged; it decides whether the usage is AllCode's cost.",
    example: "Billed, AWS paid by AllCode: AllCode pays the AWS bill and invoices the customer cost + 30 %.",
    article: "rate-plans",
    section: "billing-mode",
  },
  "terms.effectiveMonth": {
    title: "From month",
    body: "The first month the change applies to. Changes always start at a month's beginning and stay until a later entry replaces them. A month at or before a closed month cannot change.",
    example: "Choosing November 2026 bills the customer from Nov 1 00:00 UTC; October stays as it was.",
    article: "rate-plans",
    section: "billing-mode",
  },
  "terms.billingMode": {
    title: "Billing",
    body: "Billed: the customer gets an invoice each month from its rate plan. Not billed (proxy only): usage is metered and reported, but no invoice or statement is written and trials and adjustments are refused.",
    example: "A pilot customer runs not billed for two months, then is billed from the third.",
    article: "rate-plans",
    section: "billing-mode",
  },
  "terms.awsPaidBy": {
    title: "AWS paid by",
    body: "Whose AWS bill the customer's Bedrock usage lands on. Only AllCode-paid usage counts as AllCode's cost in the revenue reports and gross profit.",
    example: "AWS paid by the customer, billed on cost + 30 %: the customer pays AWS directly and AllCode's invoice is its price.",
    article: "rate-plans",
    section: "billing-mode",
  },
  "terms.planToAssign": {
    title: "Rate plan from that month",
    body: "Billing a month needs a plan of the customer's own from that month; the fleet default does not count. Choosing one assigns it in the same save. Only active plans are listed.",
    example: "Bill from November on “Standard cost + 25%” — the month is priced at list price plus 25 %.",
    article: "rate-plans",
    section: "assigning-a-plan",
  },
  "terms.costVisibility": {
    title: "Cost visibility",
    body: "Whether the customer's own admins and service keys see AWS cost in their reports and billing periods. It takes effect at once for every month, open or closed. The statement never shows cost either way.",
    example: "Hide AWS cost for a customer on a cost-plus plan who should see only its prices.",
    article: "rate-plans",
    section: "cost-visibility",
  },
  "terms.trials": {
    title: "Trials and credits",
    body: "Free usage for a billed customer, up to a dollar credit or a token allowance, optionally between two dates and for some models. Trials credit the price after discounts, never more than the customer would pay.",
    example: "A $500 credit from 1 to 30 Sep: the first $500 of September's price is free.",
    article: "trials-and-credits",
  },
  "terms.trialStarts": {
    title: "Starts on",
    body: "The first UTC day of the trial window, inclusive. Without it the trial starts the day it is created.",
    example: "2026-10-01 starts the trial at Oct 1 00:00 UTC.",
    article: "trials-and-credits",
  },
  "terms.trialEnds": {
    title: "Ends on",
    body: "The last UTC day of the trial window, inclusive. It must be on or after the start.",
    example: "2026-10-31 ends the trial at the end of Oct 31 UTC.",
    article: "trials-and-credits",
  },
  "terms.trialCredit": {
    title: "Credit",
    body: "A dollar amount credited against the customer's price, up to $10,000,000. Give a credit, a token allowance or a window — at least one.",
    example: "500 gives $500.00 of free usage.",
    article: "trials-and-credits",
  },
  "terms.trialAllowance": {
    title: "Token allowance",
    body: "A number of free tokens of any class, up to 10^12. 1-hour cache writes are not counted twice.",
    example: "250000 makes the first 250,000 tokens free.",
    article: "trials-and-credits",
  },
  "terms.trialModels": {
    title: "Models",
    body: "The Bedrock model ids the trial covers, separated by commas. Empty covers every model.",
    example: "anthropic.claude-sonnet-4-5 limits the trial to Claude Sonnet 4.5 usage.",
    article: "trials-and-credits",
  },
  "terms.adjustmentKind": {
    title: "Kind",
    body: "Credit: a dollar amount off the invoice. Write-off: waives one model and token class's price (the cost stays). Corrected tokens: adds or removes tokens for one model and class, priced at list.",
    example: "A $5 goodwill credit after an outage is a Credit.",
    article: "usage-adjustments",
  },
  "terms.adjustmentAmount": {
    title: "Amount",
    body: "The credit in dollars, greater than $0 and at most $10,000,000. It is applied after the minimum and fee.",
    example: "12.50 credits $12.50.",
    article: "usage-adjustments",
  },
  "terms.adjustmentTokenClass": {
    title: "Token class",
    body: "Which kind of token the write-off or correction applies to: input, output, cache read, or a 5-minute or 1-hour cache write.",
    example: "Output, for a correction to output tokens a retry loop double-counted.",
    article: "usage-adjustments",
  },
  "terms.adjustmentTokens": {
    title: "Tokens",
    body: "How many tokens to add (positive) or remove (negative) for the model and class. A removal is a credit at face value.",
    example: "-120000 removes 120,000 tokens that were billed twice.",
    article: "usage-adjustments",
  },
  "terms.adjustmentReason": {
    title: "Reason",
    body: "Why the adjustment is made, 10 to 500 characters. It is kept with the adjustment and in the audit log; the customer's statement never shows it.",
    example: "“Goodwill credit for the 3 Sep outage, ticket 4411.”",
    article: "usage-adjustments",
    section: "approving",
  },
  "terms.customerInvoice": {
    title: "Customer invoice",
    body: "The internal invoice: each line's AWS cost, price and profit, and the rule that priced it. It is a live preview while the month is open and fixed once it closes.",
    example: "Usage line: 12M Sonnet input tokens, AWS cost $36.00, billed $46.80, profit $10.80 (Markup).",
    article: "closing-a-month",
    section: "invoices-and-statements",
  },
  "terms.statement": {
    title: "Statement preview",
    body: "What the customer sees: line descriptions and prices only — never cost, profit, reasons or adjustment ids, whatever the cost visibility.",
    example: "“Claude Sonnet 4.5, input tokens — $46.80”, then the total.",
    article: "closing-a-month",
    section: "invoices-and-statements",
  },
  "terms.ledgerInvoice": {
    title: "Ledger invoice",
    body: "The metered usage at AWS list price, before any rate plan, with the checks the close runs (discrepancies, unverified or missing prices). It is the source the customer invoice is priced from.",
    example: "12,000,000 Sonnet input tokens at $3.00 per 1M = $36.00.",
    article: "closing-a-month",
    section: "before-you-close",
  },
  "terms.ledgerRate": {
    title: "Rate per 1M",
    body: "The AWS list price per million tokens applied to the line, from Model prices.",
    example: "$3.00 per 1M input tokens for Claude Sonnet 4.5.",
    article: "model-prices",
  },
  "terms.priceVerified": {
    title: "Price verified",
    body: "Whether the model price used was checked against AWS's published price. A month with unverified prices closes only with Force and a reason.",
    example: "A new model priced from a preview announcement shows Unverified until someone confirms it.",
    article: "closing-a-month",
    section: "forcing",
  },
} satisfies Record<string, HelpTopic>;
